import React, { useEffect, useState } from "react";
import Modal from "components/modal";
import { timezoneAbbreviationLabel } from "shared/helpers/user";
import "less/invalidate-interview-modal.less";
import clockIcon from "assets/icons/counterclockwise.png";
import cancelWhiteIcon from "assets/icons/cancel-white.png";
import cancelGreyIcon from "assets/icons/cancel-grey.png";

const defaultReasons = [
  { value: "participant_no_show", label: "Participant No Show" },
  { value: "moderator_no_show", label: "Moderator No Show" },
  { value: "other", label: "Other" },
];

const InvalidateInterviewModal = ({
  isOpen,
  onClose,
  onConfirm,
  projectName,
  participantId,
  startTime,
  endTime,
  timeZone,
  moderatorName,
  reasons = defaultReasons,
  t,
  formatDate,
  initialReschedule,
  initialReason,
  initialOtherReason,
  isAlreadyRescheduled,
}) => {
  const [reschedule, setReschedule] = useState(null);
  const [reason, setReason] = useState("");
  const [otherReason, setOtherReason] = useState("");

  // // Derive current values from local state, falling back to initial props
  // const currentReason = reason || initialReason || "";
  // const currentOtherReason =
  //   currentReason === "other"
  //     ? (otherReason !== undefined && otherReason !== ""
  //         ? otherReason
  //         : initialOtherReason || ""
  //       ).trim()
  //     : undefined;

  useEffect(() => {
    if (!isOpen) return;

    if (typeof initialReschedule === "boolean") {
      setReschedule(initialReschedule);
    } else {
      setReschedule(null);
    }
    setReason(initialReason ?? "");
    setOtherReason(initialOtherReason ?? "");
  }, [isOpen, initialReschedule, initialReason, initialOtherReason]);

  const handleConfirm = () => {
    if (typeof onConfirm === "function") {
      onConfirm({
        action: reschedule
          ? "invalidate_and_reschedule"
          : "invalidate_no_reschedule",
        reason,
        otherReason: reason === "other" ? otherReason : undefined,
      });
    }
    if (typeof onClose === "function") {
      onClose();
    }
  };

  return (
    <Modal
      title={t ? t("interview.invalidate.btn.text") : "Invalidate Interview"}
      isOpen={isOpen}
      onCancel={onClose}
      onSubmit={handleConfirm}
      shouldFocusAfterRender={false}
      submitBtnText={t ? t("incentive.credit.confirm") : "Confirm"}
      cancelBtnText={t ? t("incentive.bulk.cancel") : "Cancel"}
      enableSubmit={
        !isAlreadyRescheduled &&
        reschedule !== null &&
        Boolean(reason) &&
        (reason !== "other"
          ? true
          : Boolean(otherReason) &&
            otherReason.length > 0 &&
            otherReason.length < 150)
      }
      className="invalidate-modal"
    >
      <div className="invalidate-interview-modal">
        {isAlreadyRescheduled && (
          <div className="already-rescheduled-banner">
            <i className="im-ss-counterclockwise" />
            <span className="banner-text">INTERVIEW ALREADY RESCHEDULED</span>
          </div>
        )}
        <div className="header-block">
          <div className="project-name">{projectName}</div>
          <div className="participant-id">PARTICIPANT ID: #{participantId}</div>
        </div>

        <div className="section timeslot">
          <div className="section-title">Timeslot</div>
          <div className="date-time">
            <span className="date">
              {formatDate ? formatDate(startTime, "ddd MMM. DD, YYYY") : ""}
            </span>
            <span className="bullet">•</span>
            <span className="time strong">
              {`${formatDate ? formatDate(startTime, "h:mm A") : ""} - ${
                formatDate ? formatDate(endTime, "h:mm A") : ""
              }`}
            </span>
            <span className="tz">{timezoneAbbreviationLabel(timeZone)}</span>
          </div>

          <div
            className={`option-card ${reschedule === true ? "selected" : ""}`}
            onClick={() => {
              if (isAlreadyRescheduled) return;
              setReschedule(true);
            }}
            // style={
            //   isAlreadyRescheduled
            //     ? { pointerEvents: "none", opacity: 0.6 }
            //     : undefined
            // }
          >
            <span>
              <img
                src={clockIcon}
                alt="Invalidate"
                height={25}
              />
            </span>
            <div className="option-text">
              <span className="option-line">
                <b>Invalidate & Reschedule:</b> Email Participant to request a
                new date and time.
              </span>
            </div>
          </div>

          <div
            className={`option-card ${reschedule === false ? "selected" : ""}`}
            onClick={() => {
              if (isAlreadyRescheduled) return;
              setReschedule(false);
            }}
            // style={
            //   isAlreadyRescheduled
            //     ? { pointerEvents: "none", opacity: 0.6 }
            //     : undefined
            // }
          >
            <span>
              <img
                src={reschedule === false ? cancelGreyIcon : cancelWhiteIcon}
                alt="Reschedule Icon"
                height={23}
              />
            </span>
            <div className="option-text">
              <span className="option-line">
                <b>Invalidate & Don't Reschedule:</b> This interview will be
                removed.
              </span>
            </div>
          </div>
        </div>

        <div className="section">
          <div className="section-title">Invalidation Reason</div>
          <div
            className={`reason-select select-wrapper ${
              reason ? "selected" : ""
            }`}
          >
            <select value={reason} onChange={(e) => setReason(e.target.value)}>
              <option value="" disabled>
                {t ? t("common.select_reason") : "Select a reason"}
              </option>
              {reasons.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {reason === "other" && (
          <div className="section">
            <div className="section-title specify-reason">Specify reason</div>
            <div
              className={`reason-select select-wrapper ${
                otherReason && otherReason.trim() ? "selected" : ""
              }`}
            >
              <textarea
                value={otherReason}
                onChange={(e) => setOtherReason(e.target.value)}
                maxLength={150}
                placeholder={
                  t
                    ? t("interview.invalidate.specify_reason_placeholder")
                    : "Enter reason for invalidation"
                }
              />
              {otherReason && otherReason.trim().length >= 150 && (
                <div className="input-error">
                  {t
                    ? t("interview.invalidate.reason_length_error")
                    : "Reason must be less than 150 characters."}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="section">
          <div className="section-title">Moderators</div>
          <div className="moderator-card">
            <i className="im-user2 avatar" />
            <div className="moderator-info">
              <div className="moderator-label">Moderator 1:
                <span className="moderator-name"> {moderatorName}</span>
              </div>
            </div>
            <i className="im-link2 link-icon" />
            <i className="im-ss-delete remove-icon" />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default InvalidateInterviewModal;
